/* AVAL GLOBAL RUNTIME
 * Execution order preserved: aval-runtime.js -> aval-header.js
 */

/* === Runtime and external-service loader === */
(function () {
  'use strict';

  const namespace = window.AVAL = window.AVAL || {};

  function safeJSONParse(value, fallback) {
    try {
      return JSON.parse(String(value || ''));
    } catch (error) {
      return fallback === undefined ? null : fallback;
    }
  }

  function readConfig(id) {
    const node = document.getElementById(id);
    if (!node) return {};
    return safeJSONParse(node.textContent, {}) || {};
  }

  function addResourceHint(rel, href, crossOrigin) {
    if (!href || document.head.querySelector(`link[rel="${rel}"][href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = rel;
    link.href = href;
    if (crossOrigin) link.crossOrigin = crossOrigin;
    document.head.appendChild(link);
  }

  function loadScriptOnce(src, options) {
    if (!src) return Promise.reject(new Error('Script URL fehlt.'));

    const existing = Array.from(document.scripts).find((script) => script.src === src);
    if (existing && existing.dataset.avalLoaded === 'true') return Promise.resolve(existing);

    return new Promise((resolve, reject) => {
      const script = existing || document.createElement('script');
      let settled = false;

      function complete(ok, value) {
        if (settled) return;
        settled = true;
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
        if (ok) {
          script.dataset.avalLoaded = 'true';
          resolve(value || script);
        } else {
          reject(value instanceof Error ? value : new Error('Script konnte nicht geladen werden.'));
        }
      }

      function onLoad() { complete(true, script); }
      function onError() { complete(false, new Error(`Script konnte nicht geladen werden: ${src}`)); }

      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });

      if (!existing) {
        script.src = src;
        script.async = options?.async !== false;
        script.defer = options?.defer !== false;
        if (options?.crossOrigin) script.crossOrigin = options.crossOrigin;
        if (options?.referrerPolicy) script.referrerPolicy = options.referrerPolicy;
        if (options?.dataset) {
          Object.entries(options.dataset).forEach(([key, value]) => {
            script.dataset[key] = String(value);
          });
        }
        document.head.appendChild(script);
      }
    });
  }

  function fetchWithTimeout(input, init, timeoutMs) {
    const timeout = Number.isFinite(Number(timeoutMs)) ? Math.max(1000, Number(timeoutMs)) : 15000;
    if (!window.AbortController) return window.fetch(input, init);

    const controller = new AbortController();
    const options = Object.assign({}, init || {}, { signal: controller.signal });
    const upstreamSignal = init?.signal;
    const abortFromUpstream = () => controller.abort(upstreamSignal?.reason);

    if (upstreamSignal) {
      if (upstreamSignal.aborted) abortFromUpstream();
      else upstreamSignal.addEventListener('abort', abortFromUpstream, { once: true });
    }

    const timer = window.setTimeout(() => controller.abort(), timeout);

    return window.fetch(input, options).finally(() => {
      window.clearTimeout(timer);
      if (upstreamSignal) upstreamSignal.removeEventListener('abort', abortFromUpstream);
    });
  }

  function createDeferred() {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  }

  const config = readConfig('aval-runtime-config');
  namespace.config = Object.freeze(config);
  namespace.safeJSONParse = safeJSONParse;
  namespace.readConfig = readConfig;
  namespace.loadScriptOnce = loadScriptOnce;
  namespace.fetchWithTimeout = fetchWithTimeout;

  /* Google Maps/Places: only requested when the visitor uses an address feature. */
  const mapsDeferred = createDeferred();
  let mapsState = 'idle';
  let mapsPromise = mapsDeferred.promise;

  function hasGooglePlaces() {
    return Boolean(window.google && window.google.maps && window.google.maps.places);
  }

  function settleMaps(success) {
    if (mapsState === 'ready' || mapsState === 'failed') return;
    mapsState = success ? 'ready' : 'failed';
    mapsDeferred.resolve(Boolean(success));
    document.dispatchEvent(new CustomEvent('gmaps:ready', { detail: { success: Boolean(success) } }));
  }

  if (hasGooglePlaces()) {
    mapsState = 'ready';
    mapsDeferred.resolve(true);
  }

  window.__avalGmapsReady = mapsPromise;
  window.__gmapsReady = mapsPromise;

  namespace.loadMaps = function loadMaps() {
    if (hasGooglePlaces()) {
      settleMaps(true);
      return Promise.resolve(true);
    }

    if (mapsState === 'loading' || mapsState === 'ready' || mapsState === 'failed') return mapsPromise;

    const maps = config.maps || {};
    const apiKey = String(maps.apiKey || '').trim();
    if (maps.enabled === false || !apiKey) {
      settleMaps(false);
      return mapsPromise;
    }

    mapsState = 'loading';
    addResourceHint('preconnect', 'https://maps.googleapis.com', 'anonymous');
    addResourceHint('preconnect', 'https://maps.gstatic.com', 'anonymous');

    const callbackName = '__avalGoogleMapsLoaded';
    window[callbackName] = function () {
      try { delete window[callbackName]; } catch (error) { window[callbackName] = undefined; }
      settleMaps(hasGooglePlaces());
    };

    const params = new URLSearchParams({
      key: apiKey,
      libraries: 'places',
      language: String(maps.language || 'de'),
      region: String(maps.region || 'DE'),
      callback: callbackName,
      loading: 'async'
    });

    const src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    loadScriptOnce(src, { async: true, defer: true, referrerPolicy: 'strict-origin-when-cross-origin' })
      .catch(() => settleMaps(false));

    window.setTimeout(() => {
      if (mapsState === 'loading') settleMaps(hasGooglePlaces());
    }, 15000);

    return mapsPromise;
  };

  const mapTriggerSelector = [
    '#hqp-pickup', '#hqp-dropoff', '#hqp-calc',
    '.js-abholadresse', '.js-zieladresse', '#calcPriceBtn',
    '#adresse', '#availableLocation', '.js-block-location',
    '[data-aval-load-maps]'
  ].join(',');

  function requestMapsFromEvent(event) {
    const target = event.target instanceof Element ? event.target.closest(mapTriggerSelector) : null;
    if (target) namespace.loadMaps();
  }

  if (config.maps?.needed) {
    document.addEventListener('focusin', requestMapsFromEvent, { passive: true });
    document.addEventListener('pointerdown', requestMapsFromEvent, { passive: true });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') requestMapsFromEvent(event);
    });
    document.addEventListener('aval:maps:request', () => namespace.loadMaps());

    if (config.maps.lazy === false) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => namespace.loadMaps(), { once: true });
      } else {
        namespace.loadMaps();
      }
    }
  } else {
    settleMaps(false);
  }

  /* Google Ads: basic Consent Mode; the vendor script loads only with marketing permission. */
  const ads = config.ads || {};
  const adsId = String(ads.id || '').trim();
  let adsLoaded = false;
  let adsConsentInitialized = false;
  let privacyApiRequested = false;

  function ensureGtagQueue() {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  }

  function setAdsConsent(granted, useDefault) {
    ensureGtagQueue();
    const mode = useDefault === true || !adsConsentInitialized ? 'default' : 'update';
    window.gtag('consent', mode, {
      ad_storage: granted ? 'granted' : 'denied',
      analytics_storage: 'denied',
      ad_user_data: granted ? 'granted' : 'denied',
      ad_personalization: granted ? 'granted' : 'denied',
      wait_for_update: granted ? 0 : 500
    });
    adsConsentInitialized = true;
  }

  function startAds() {
    if (!adsId || ads.enabled === false) return Promise.resolve(false);

    setAdsConsent(true);
    if (adsLoaded) return Promise.resolve(true);
    adsLoaded = true;

    ensureGtagQueue();
    window.gtag('js', new Date());
    window.gtag('config', adsId, { send_page_view: true });

    const src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(adsId)}`;
    addResourceHint('preconnect', 'https://www.googletagmanager.com', 'anonymous');
    return loadScriptOnce(src, { async: true, defer: true })
      .then(() => true)
      .catch(() => {
        adsLoaded = false;
        return false;
      });
  }

  function marketingAllowed() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.marketingAllowed !== 'function') return ads.requireConsent !== true;
    try {
      return privacy.marketingAllowed() === true;
    } catch (error) {
      return false;
    }
  }

  function evaluateAdsConsent() {
    const allowed = marketingAllowed();
    setAdsConsent(allowed);
    if (allowed) startAds();
  }

  function requestPrivacyApi() {
    if (privacyApiRequested) return;
    privacyApiRequested = true;

    if (window.Shopify?.customerPrivacy && typeof window.Shopify.customerPrivacy.marketingAllowed === 'function') {
      evaluateAdsConsent();
      return;
    }

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      if (ads.requireConsent !== true) startAds();
      return;
    }

    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        if (error) {
          if (ads.requireConsent !== true) startAds();
          return;
        }
        evaluateAdsConsent();
      }
    );
  }

  if (ads.enabled !== false && adsId) {
    setAdsConsent(false, true);
    document.addEventListener('visitorConsentCollected', evaluateAdsConsent);
    document.addEventListener('shopifyCustomerPrivacyApiLoaded', evaluateAdsConsent);

    if (ads.requireConsent === true) requestPrivacyApi();
    else startAds();
  }
})();


/* === Header controller === */
(function(){
  class AvalHeader extends HTMLElement{
    constructor(){
      super();
      this.openTimer=null;
      this.closeTimer=null;
      this.resizeObserver=null;
      this.previousBodyOverflow='';
      this.inertState=[];
      this.lastFocusedElement=null;
      this.onDocumentKeydown=this.onDocumentKeydown.bind(this);
      this.onDocumentPointerDown=this.onDocumentPointerDown.bind(this);
      this.onDocumentFocusIn=this.onDocumentFocusIn.bind(this);
      this.onScroll=this.onScroll.bind(this);
      this.updateOffsets=this.updateOffsets.bind(this);
    }

    connectedCallback(){
      this.wrap=this.querySelector('.aval-header-wrap');
      this.desktopMenus=Array.from(this.querySelectorAll('[data-aval-menu]'));
      this.mobileMenu=this.querySelector('[data-aval-mobile]');
      this.mobileTrigger=this.mobileMenu?.querySelector(':scope > summary') || null;
      this.mobilePanel=this.querySelector('[data-aval-mobile-panel]');
      this.closeButtons=Array.from(this.querySelectorAll('[data-aval-close]'));

      this.bindDesktopMenus();
      this.bindMobileMenu();
      this.updateOffsets();
      this.syncExpanded();

      document.addEventListener('keydown',this.onDocumentKeydown);
      document.addEventListener('pointerdown',this.onDocumentPointerDown);
      document.addEventListener('focusin',this.onDocumentFocusIn);
      window.addEventListener('scroll',this.onScroll,{passive:true});
      window.addEventListener('resize',this.updateOffsets,{passive:true});

      this.observeResize();
      this.onScroll();
    }

    disconnectedCallback(){
      document.removeEventListener('keydown',this.onDocumentKeydown);
      document.removeEventListener('pointerdown',this.onDocumentPointerDown);
      document.removeEventListener('focusin',this.onDocumentFocusIn);
      window.removeEventListener('scroll',this.onScroll);
      window.removeEventListener('resize',this.updateOffsets);
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if(this.resizeObserver) this.resizeObserver.disconnect();
      this.setBackgroundInert(false);
      this.unlockBody();
    }

    isDesktop(){
      return window.matchMedia('(min-width:1241px)').matches;
    }

    bindDesktopMenus(){
      this.desktopMenus.forEach((details)=>{
        const summary=details.querySelector(':scope > summary');

        details.addEventListener('toggle',()=>{
          if(details.open) this.closeDesktopMenus(details);
          this.syncExpanded();
        });

        details.addEventListener('mouseenter',()=>{
          if(!this.isDesktop()) return;
          clearTimeout(this.closeTimer);
          this.openTimer=setTimeout(()=>{
            this.closeDesktopMenus(details);
            details.open=true;
            this.syncExpanded();
          },80);
        });

        details.addEventListener('mouseleave',()=>{
          if(!this.isDesktop()) return;
          clearTimeout(this.openTimer);
          this.closeTimer=setTimeout(()=>{
            if(!details.matches(':hover') && !details.matches(':focus-within')){
              details.open=false;
              this.syncExpanded();
            }
          },170);
        });

        details.addEventListener('focusout',()=>{
          if(!this.isDesktop()) return;
          window.setTimeout(()=>{
            if(details.open && !details.contains(document.activeElement) && !details.matches(':hover')){
              details.open=false;
              this.syncExpanded();
            }
          },0);
        });

        summary?.addEventListener('click',()=>window.setTimeout(()=>this.syncExpanded(),0));
      });
    }

    bindMobileMenu(){
      if(!this.mobileMenu) return;

      this.mobileMenu.addEventListener('toggle',()=>{
        this.setMobileState(this.mobileMenu.open);
      });

      this.closeButtons.forEach((button)=>{
        button.addEventListener('click',()=>this.closeMobileMenu(true));
      });

      if(this.mobilePanel){
        this.mobilePanel.addEventListener('click',(event)=>{
          if(event.target===this.mobilePanel) this.closeMobileMenu(true);
        });
      }

      this.querySelectorAll('.aval-drawer a').forEach((link)=>{
        link.addEventListener('click',()=>this.closeMobileMenu(false));
      });
    }

    setMobileState(isOpen){
      this.syncExpanded();

      if(isOpen){
        this.lastFocusedElement=document.activeElement instanceof HTMLElement
          ? document.activeElement
          : this.mobileTrigger;
        this.closeDesktopMenus();
        this.lockBody();
        this.setBackgroundInert(true);
        this.mobileTrigger?.setAttribute('aria-label','Menü schließen');

        window.requestAnimationFrame(()=>{
          if(!this.mobileMenu?.open) return;
          const first=this.getPanelFocusableElements()[0] || this.mobilePanel;
          first?.focus({preventScroll:true});
        });
      }else{
        this.mobileTrigger?.setAttribute('aria-label','Menü öffnen');
        this.setBackgroundInert(false);
        this.unlockBody();
      }
    }

    closeDesktopMenus(except){
      this.desktopMenus.forEach((details)=>{
        if(details!==except) details.open=false;
      });
      this.syncExpanded();
    }

    closeMobileMenu(restoreFocus){
      if(!this.mobileMenu) return;
      const wasOpen=this.mobileMenu.open;
      this.mobileMenu.open=false;
      this.setMobileState(false);

      if(wasOpen && restoreFocus!==false){
        const focusTarget=this.mobileTrigger || this.lastFocusedElement;
        window.requestAnimationFrame(()=>focusTarget?.focus({preventScroll:true}));
      }
    }

    syncExpanded(){
      this.querySelectorAll('summary[aria-expanded]').forEach((summary)=>{
        const details=summary.closest('details');
        summary.setAttribute('aria-expanded',details?.open ? 'true':'false');
      });
    }

    getPanelFocusableElements(){
      if(!this.mobilePanel) return [];
      const selector=[
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'details > summary:first-of-type',
        '[tabindex]:not([tabindex="-1"])'
      ].join(',');

      return Array.from(this.mobilePanel.querySelectorAll(selector)).filter((element)=>{
        if(!(element instanceof HTMLElement)) return false;
        if(element.hidden || element.getAttribute('aria-hidden')==='true') return false;
        return element.getClientRects().length>0;
      });
    }

    getMobileFocusCycle(){
      const elements=[];
      if(this.mobileTrigger && this.mobileTrigger.getClientRects().length>0) elements.push(this.mobileTrigger);
      this.getPanelFocusableElements().forEach((element)=>{
        if(!elements.includes(element)) elements.push(element);
      });
      return elements;
    }

    trapMobileFocus(event){
      const cycle=this.getMobileFocusCycle();
      if(!cycle.length) return;

      const first=cycle[0];
      const last=cycle[cycle.length-1];
      const active=document.activeElement;
      const activeIndex=cycle.indexOf(active);

      if(event.shiftKey){
        if(activeIndex<=0){
          event.preventDefault();
          last.focus();
        }
      }else if(activeIndex===-1 || activeIndex===cycle.length-1){
        event.preventDefault();
        first.focus();
      }
    }

    onDocumentKeydown(event){
      if(event.key==='Tab' && this.mobileMenu?.open){
        this.trapMobileFocus(event);
        return;
      }

      if(event.key!=='Escape') return;

      if(this.mobileMenu?.open){
        event.preventDefault();
        this.closeMobileMenu(true);
        return;
      }

      const openDesktop=this.desktopMenus.find((details)=>details.open);
      if(openDesktop){
        event.preventDefault();
        const summary=openDesktop.querySelector(':scope > summary');
        openDesktop.open=false;
        this.syncExpanded();
        summary?.focus();
      }
    }

    onDocumentPointerDown(event){
      if(!this.isDesktop()) return;
      const target=event.target;
      if(!(target instanceof Node)) return;

      const openDesktop=this.desktopMenus.find((details)=>details.open);
      if(openDesktop && !openDesktop.contains(target)) this.closeDesktopMenus();
    }

    onDocumentFocusIn(event){
      if(!this.mobileMenu?.open) return;
      const target=event.target;
      if(target instanceof Node && this.mobileMenu.contains(target)) return;

      const first=this.getPanelFocusableElements()[0] || this.mobilePanel || this.mobileTrigger;
      first?.focus({preventScroll:true});
    }

    onScroll(){
      this.classList.toggle('is-scrolled',window.scrollY>6);
    }

    lockBody(){
      if(!document.documentElement.classList.contains('aval-drawer-is-open')){
        this.previousBodyOverflow=document.body.style.overflow;
      }
      document.documentElement.classList.add('aval-drawer-is-open');
      document.body.style.overflow='hidden';
    }

    unlockBody(){
      document.documentElement.classList.remove('aval-drawer-is-open');
      document.body.style.overflow=this.previousBodyOverflow || '';
      this.previousBodyOverflow='';
    }

    setBackgroundInert(active){
      if(!('inert' in HTMLElement.prototype)) return;

      if(active){
        if(this.inertState.length) return;
        this.inertState=Array.from(document.body.children)
          .filter((element)=>element instanceof HTMLElement)
          .filter((element)=>!element.contains(this))
          .filter((element)=>!['SCRIPT','STYLE','TEMPLATE'].includes(element.tagName))
          .map((element)=>({element,wasInert:element.inert}));
        this.inertState.forEach(({element})=>{ element.inert=true; });
        return;
      }

      this.inertState.forEach(({element,wasInert})=>{ element.inert=wasInert; });
      this.inertState=[];
    }

    updateOffsets(){
      const announcement=document.querySelector('announcement-bar, .announcement-bar');
      let height=0;
      if(announcement){
        const style=window.getComputedStyle(announcement);
        if(style.display!=='none' && style.visibility!=='hidden'){
          height=Math.ceil(announcement.getBoundingClientRect().height);
        }
      }
      document.documentElement.style.setProperty('--aval-announcement-height',height+'px');

      if(this.wrap){
        const headerHeight=Math.ceil(this.wrap.getBoundingClientRect().height);
        document.documentElement.style.setProperty('--header-height',headerHeight+'px');
        document.documentElement.style.setProperty('--aval-header-height',headerHeight+'px');
      }

      if(this.isDesktop() && this.mobileMenu?.open) this.closeMobileMenu(false);
    }

    observeResize(){
      if(!('ResizeObserver' in window)) return;
      this.resizeObserver=new ResizeObserver(()=>this.updateOffsets());
      if(this.wrap) this.resizeObserver.observe(this.wrap);
      const announcement=document.querySelector('announcement-bar, .announcement-bar');
      if(announcement) this.resizeObserver.observe(announcement);
    }
  }

  if(!customElements.get('aval-header')) customElements.define('aval-header',AvalHeader);
})();
